(ns web3.components.select
  (:require [web3.components.text :as text]
            [web3.components.progress :as progress]
            [reagent.core :as r]
            [web3.icons.shapes :as shapes]
            [web3.view.hiccup :as hiccup]))

(defn get-select-label
  "Returns label for `value` in the `options` for a select element.

  Returns `value` if label not found."
  [value options]
  (when value
    (or (->> (tree-seq #(and (coll? %) (or (not (vector? %))
                                           (= :optgroup (first %))))
                       (fn [x]
                         (if (vector? x)
                           (rest x)
                           x))
                       options)
             (keep #(when (and (vector? %)
                               (= :option (first %))
                               (= (get-in % [1 :value]) value))
                      (last (drop 2 %))))
             (first))
        value)))

(comment
 (def sample-opts (list [:option {:value "a"} "A"]
                        [:option {:value "b"} "B"]
                        [:optgroup {:label "numbers"}
                         [:option {:value 1} "One"]
                         [:option {:value 2} "Two"]]))
 (assert (= "A" (get-select-label "a" sample-opts)))
 (assert (= "B" (get-select-label "b" sample-opts)))
 (assert (= "One" (get-select-label 1 sample-opts)))
 (assert (= "Two" (get-select-label 2 sample-opts))))

(defn select [{:keys [read-only?
                      loading?
                      value
                      default-value
                      label

                      error?
                      invalid?

                      on-focus
                      on-blur

                      helptext
                      helptext-persistent?] :as props} & options]
  (let [{:keys [state]} (r/current-component)
        {:keys [focused?]} @state
        invalid? (or error? invalid?)]
    (if read-only?
      [text/input (-> props
                      (dissoc :loading?)
                      (assoc :on-change #())
                      (assoc :value (or (get-select-label (or value default-value) options)
                                        "")))]
      [:div
       [:div.mdc-textfield.mdc-textfield--editable.mdc-textfield--upgraded.relative.width-100p
        {:class (str (when invalid? " mdc-textfield--invalid")
                     (when focused? " mdc-textfield--focused"))
         :style (when loading? {:border-color "transparent"})}
        [:div.text-dark-gray.mdc-textfield__label.mdc-textfield__label--float-above
         label]
        [:select.mdc-select.text-size-14.font-proba
         (-> props
             (dissoc :read-only?
                     :loading?
                     :helptext
                     :helptext-persistent?
                     :default-value
                     :dirty?
                     :invalid?
                     :error?)
             (update :value (fn [x] (or x default-value "")))
             (merge {:on-focus #(do (swap! state assoc :focused? true)
                                    (when on-focus (on-focus %)))
                     :on-blur #(do (swap! state dissoc :focused?)
                                   (when on-blur (on-blur %)))})
             (assoc-in [:style :border-radius] 0))
         options]
        (if loading?
          [progress/indeterminate {:height 2
                                   :key "progress"
                                   :class "absolute left-0 right-0 z-9 text-blue"}]
          [:div.mdc-textfield__bottom-line {:key "bottom-bar"
                                            :class (when focused? "mdc-textfield__bottom-line--active")}])]
       [:div.mdc-textfield-helptext {:aria-hidden true
                                     :key "label"
                                     :class (when helptext-persistent? "mdc-textfield-helptext--persistent")}
        helptext]])))

(defn add-list-item
  [{:keys [more? selected?] :as props} title]
  [:a.list-item.pad-v-3.text-dark-gray.hover-text-black-hover.flex.flex-column.relative.cursor-pointer
   (-> props
       (dissoc :more? :selected?))
   [:div.flex.flex-auto
    title]
   [:div.inline-flex.absolute.right-0
    (cond
      selected? shapes/checkmark-thin
      more? shapes/chevron-right
      :else nil)]])

(defn selectable-dropdown
  [{:keys [key
           title
           selected?
           expanded?
           on-select
           more?
           expanded?
           on-expand-toggle
           class
           text-class
           nav-axis]
    :or {nav-axis :horizontal}}]
  [:div.selectable-dropdown
   {:key key
    :class (or class "")}
   [:div.border
    [:div.content.flex.items-stretch.flex-none
     [:a.flex.items-center.flex-auto.hover-appear-parent
      {:on-click on-select
       :style {:transition "0.1s ease color"}
       :class (cond-> (if selected?
                        "text-black"
                        "text-dark-gray hover-text-black-hover")
                      on-select (str " cursor-pointer")
                      text-class (str " " text-class))}
      [:div.flex.items-center.cursor-pointer.pad-right-2.pad-left-1
       (-> shapes/checkmark-thin
           (hiccup/class (str (if selected?
                               "text-blue"
                               "text-gray hover-appear-child"))))]
      [:div.flex-auto.pad-left-2.pad-v-3
       title]]
     (when on-expand-toggle
       [:a.flex.items-center.text-gray.pad-left-4
        {:on-click on-expand-toggle
         :class (when on-expand-toggle
                  "cursor-pointer hover-text-black")}
        (if expanded?
          (case nav-axis :vertical shapes/chevron-up
                         :horizontal shapes/chevron-left)
          (case nav-axis :vertical shapes/chevron-down
                         :horizontal shapes/chevron-right))])]]])

(defn selectable-property-row
  [{:keys [key
           ref
           title
           selected?
           on-select
           expanded?
           class
           text-class
           nav-axis
           edit?]
    :or {nav-axis :horizontal}}]
  [:a.selectable-dropdown
   {:on-click on-select
    :key key
    :ref ref
    :class (or class "")}
   [:div.border
    [:div.content.flex.items-stretch.flex-none
     [:div.flex.items-center.flex-auto.hover-appear-parent
      {:style {:transition "0.1s ease color"}
       :class (cond-> (if selected?
                        "text-black"
                        "text-dark-gray hover-text-black-hover")
                on-select (str " cursor-pointer")
                text-class (str " " text-class))}
      [:div.flex.items-center.cursor-pointer.pad-right-2.pad-left-1
       {:style {:min-width 30}}
       (when-not edit?
         (-> shapes/checkmark-thin
             (hiccup/class (str (if selected?
                                 "text-blue"
                                 "text-gray hover-appear-child")))))]
      [:div.flex-auto.pad-left-2.pad-v-3
       title]]
     (when edit?
      [:div.flex.items-center.text-blue.pad-left-4
       (if expanded?
         (case nav-axis :vertical shapes/chevron-up
                        :horizontal shapes/chevron-left)
         (case nav-axis :vertical shapes/chevron-down
                        :horizontal shapes/chevron-right))])]]])